import {Button, Select} from '@mantine/core';
import type {FC} from 'react';
import {MdArrowBack, MdList} from 'react-icons/md';
import {COLOR_PRIMARY_1} from '../../../../../config/constants/colorCodes';
import type {WorkAttractiveConditionType} from '../../../../../core/entities/company/schema';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {
  ENTRY_BUTTON_ID,
  GradientEntryButton,
} from '../../../../../shared/components/EntryButton/GradientEntryButton';
import {buildCompanyFollowGtmContent} from '../../../../../shared/services/buildCompanyFollowGtmContent';
import {FollowCompanyButton} from '../../FollowButton';
import {AttractiveConditions} from './AttractiveConditions';

type Props = {
  companyLogoURL?: string;
  companyName: string;
  industryName: string;
  isFollowed: boolean;
  companyId: number;
  companyFollowerCount: number;
  followCount: number;
  onFollow: () => void | Promise<void>;
  recruitingFairRegistrationURL?: string;
  entryURL?: string;
  crumbsHref: string;
  isEngineerCompany: boolean;
  crumbIndustryName: string;
  year: string;
  availableGraduationYears: number[];
  mainPhotoURL?: string;
  vimeoId?: string;
  isValid: boolean;
  openTableOfContentsModal: () => void;
  workAttractiveConditions: WorkAttractiveConditionType[];
};
export const CompanyHeaderSP: FC<Props> = ({
  companyLogoURL,
  companyName,
  industryName,
  isFollowed,
  companyId,
  companyFollowerCount,
  followCount,
  onFollow,
  recruitingFairRegistrationURL,
  entryURL,
  crumbsHref,
  isEngineerCompany,
  crumbIndustryName,
  year,
  availableGraduationYears,
  mainPhotoURL,
  vimeoId,
  isValid,
  openTableOfContentsModal,
  workAttractiveConditions,
}) => {
  return (
    <>
      <div className="flex bg-white-1 rounded-16 shadow-card p-16">
        <CommonLink href={crumbsHref}>
          <MdArrowBack className="bg-background rounded-16" size={24} />
        </CommonLink>
        <p className="ml-8 font-bold text-lg">
          {isEngineerCompany ? 'エンジニア' : crumbIndustryName}
        </p>
      </div>
      <div className="flex flex-col bg-white-1 rounded-16 shadow-card mt-8">
        <div className="flex mt-16 px-16">
          {companyLogoURL && (
            <img
              src={companyLogoURL}
              alt={`${companyName}のロゴ`}
              className="w-[48px] h-[36px]"
            />
          )}
          <div className="pl-16">
            <h1 className="font-bold">{companyName}</h1>
            <div className="text-sm">{industryName}</div>
          </div>
        </div>
        {workAttractiveConditions && (
          <AttractiveConditions
            workAttractiveConditions={workAttractiveConditions}
          />
        )}
        <div className="flex justify-end gap-8 h-40 px-16 mt-12">
          <FollowCompanyButton
            companyId={companyId}
            initial={{
              followerCount: companyFollowerCount,
              isFollowed,
            }}
            hasPlus={false}
            controlled={{
              isFollowed,
              followCount,
              onFollow,
            }}
            dataGtmContent={buildCompanyFollowGtmContent({
              companyId,
              companyName,
              location: 'company-detail',
            })}
          />
          {recruitingFairRegistrationURL && (
            <CommonLink
              href={recruitingFairRegistrationURL}
              target="_blank"
              rel="noopener noreferrer"
              id="recruiting_info_entry_fair_button"
            >
              <Button className="px-16 h-full bg-white text-primary-1 border-primary-1 rounded-[8px] text-sm font-bold hover:ease-in-out hover:duration-300">
                <span className="leading block w-full text-center font-bold">
                  説明会を予約
                </span>
              </Button>
            </CommonLink>
          )}
          {entryURL && (
            <GradientEntryButton
              entryUrl={entryURL}
              id={ENTRY_BUTTON_ID.COMPANY_ENTRY_SP}
              withShadow
              companyId={companyId}
              companyName={companyName}
            />
          )}
        </div>
        {mainPhotoURL &&
          (vimeoId ? (
            <iframe
              title={`${companyName}の映像`}
              src={`https://player.vimeo.com/video/${vimeoId}`}
              className="w-full mt-16"
            />
          ) : (
            <div className="sm:px-0 mt-16">
              <img
                src={mainPhotoURL}
                alt={`${companyName}の画像`}
                className="w-full"
              />
            </div>
          ))}
        <div className="flex justify-between px-16 py-12">
          <button
            type="button"
            className="text-base font-bold rounded-[20px] flex items-center px-8"
            onClick={openTableOfContentsModal}
          >
            <div className="flex h-20 text-primary-1">
              <MdList size={20} /> <span>目次</span>
            </div>
          </button>
          {!isValid &&
            availableGraduationYears &&
            availableGraduationYears.length > 0 && (
              <Select
                className={`${!isValid && 'hidden'}`}
                placeholder="卒年を選択"
                defaultValue={year}
                data={availableGraduationYears?.map((item) => {
                  return {value: String(item), label: `${item}年卒`};
                })}
                onChange={(value) => {
                  location.href = `/company/${companyId}/${value}`; // TODO: check if this value is correct
                }}
                styles={{
                  input: {
                    borderWidth: 0,
                    color: COLOR_PRIMARY_1,
                    fontWeight: 'bold',
                    width: 128,
                  },
                }}
              />
            )}
        </div>
      </div>
    </>
  );
};
